import { addDoc, collection, doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { useState, useEffect } from "react";
import { db, storage } from "../firebase/config";
import Header from "../Components/Header";
import toast from "react-hot-toast";

const AIToolsForm = ({ 
  setIsSidebarOpen, 
  isSidebarOpen, 
  editingTool = null, 
  onSuccess = null, 
  onCancel = null, 
  isModal = false 
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    link: "",
    categories: [],
    logo: "",
  });

  const [category, setCategory] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Initialize form data when editing
  useEffect(() => {
    if (editingTool) {
      setFormData({
        name: editingTool.name || "",
        description: editingTool.description || "",
        link: editingTool.link || "",
        categories: editingTool.categories || [],
        logo: editingTool.logo || "",
      });
      setLogoPreview(editingTool.logo || "");
    } else {
      setFormData({
        name: "",
        description: "",
        link: "",
        categories: [],
        logo: "",
      });
      setLogoPreview("");
    }
  }, [editingTool]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error("Veuillez sélectionner un fichier image valide.");
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("La taille du fichier ne doit pas dépasser 5MB.");
        return;
      }

      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadLogo = async () => {
    if (!logoFile) return formData.logo;

    setUploadingLogo(true);
    try {
      // Create a unique filename
      const timestamp = Date.now();
      const filename = `logos/${timestamp}_${logoFile.name}`;
      const storageRef = ref(storage, filename);

      // Upload file
      await uploadBytes(storageRef, logoFile);
      
      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error("Erreur lors du téléchargement du logo.");
      throw error;
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview("");
    setFormData({
      ...formData,
      logo: "",
    });
  };

  const handleAddCategory = () => {
    if (
      category.trim() !== "" &&
      !formData.categories.includes(category.trim())
    ) {
      setFormData({
        ...formData,
        categories: [...formData.categories, category.trim()],
      });
      setCategory("");
    }
  };

  const handleRemoveCategory = (catToRemove) => {
    setFormData({
      ...formData,
      categories: formData.categories.filter((cat) => cat !== catToRemove),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Upload logo if there's a new file
      let logoURL = formData.logo;
      if (logoFile) {
        logoURL = await uploadLogo();
      }

      const dataToSave = {
        ...formData,
        logo: logoURL,
      };

      if (editingTool) {
        // Update existing tool
        const docRef = doc(db, "aitools", editingTool.id);
        await updateDoc(docRef, {
          ...dataToSave,
          updatedAt: new Date(),
        });
        toast.success("Outil IA modifié avec succès !");
      } else {
        // Add new tool
        const collectionRef = collection(db, "aitools");
        await addDoc(collectionRef, {
          ...dataToSave,
          createdAt: new Date(),
        });
        toast.success("Outil IA ajouté avec succès !");
      }

      // Reset form if not editing
      if (!editingTool) {
        setFormData({
          name: "",
          description: "",
          link: "",
          categories: [],
          logo: "",
        });
        setLogoFile(null);
        setLogoPreview("");
      }

      // Call success callback if provided (for modal usage)
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      toast.error(editingTool ? "Erreur lors de la modification de l'outil IA." : "Erreur lors de l'ajout de l'outil IA.");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddCategory();
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  // Form content component
  const FormContent = () => (
    <form onSubmit={handleSubmit} className={isModal ? "space-y-4" : ""}>
      <div className={isModal ? "" : "mb-4"}>
        <label className={`block text-start text-sm font-medium mb-2 ${isModal ? 'text-white' : ''}`}>
          Nom
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          className={`w-full p-3 bg-[#1e293b] rounded border border-gray-700 focus:border-blue-500 focus:outline-none ${isModal ? 'text-white' : ''}`}
          required
          placeholder="Nom de l'outil"
        />
      </div>

      <div className={isModal ? "" : "mb-4"}>
        <label className={`block text-start text-sm font-medium mb-2 ${isModal ? 'text-white' : ''}`}>
          Description
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          className={`w-full p-3 bg-[#1e293b] rounded border border-gray-700 focus:border-blue-500 focus:outline-none ${isModal ? 'text-white' : ''}`}
          rows="3"
          required
          placeholder="Description de l'outil"
        />
      </div>

      <div className={isModal ? "" : "mb-4"}>
        <label className={`block text-start text-sm font-medium mb-2 ${isModal ? 'text-white' : ''}`}>
          Lien
        </label>
        <input
          type="url"
          name="link"
          value={formData.link}
          onChange={handleInputChange}
          className={`w-full p-3 bg-[#1e293b] rounded border border-gray-700 focus:border-blue-500 focus:outline-none ${isModal ? 'text-white' : ''}`}
          required
          placeholder="https://example.com"
        />
      </div>

      {/* Logo Upload Section */}
      <div className={isModal ? "" : "mb-4"}>
        <label className={`block text-start text-sm font-medium mb-2 ${isModal ? 'text-white' : ''}`}>
          Logo
        </label>
        <div className="space-y-3">
          <input
            type="file"
            accept="image/*"
            onChange={handleLogoChange}
            className={`w-full p-3 bg-[#1e293b] rounded border border-gray-700 focus:border-blue-500 focus:outline-none ${isModal ? 'text-white' : ''} file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700`}
          />
          {logoPreview && (
            <div className="flex items-center gap-3">
              <img
                src={logoPreview}
                alt="Logo preview"
                className="w-16 h-16 object-cover rounded border border-gray-600"
              />
              <button
                type="button"
                onClick={handleRemoveLogo}
                className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
              >
                Supprimer
              </button>
            </div>
          )}
          <p className="text-xs text-gray-400">
            Formats acceptés: JPG, PNG, GIF. Taille max: 5MB
          </p>
        </div>
      </div>

      <div className={isModal ? "" : "mb-6"}>
        <label className={`block text-start text-sm font-medium mb-2 ${isModal ? 'text-white' : ''}`}>
          Catégories
        </label>
        <div className="flex">
          <input
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            onKeyDown={handleKeyDown}
            className={`flex-1 p-3 bg-[#1e293b] rounded-l border border-gray-700 focus:border-blue-500 focus:outline-none ${isModal ? 'text-white' : ''}`}
            placeholder="Ajouter une catégorie"
          />
          <button
            type="button"
            onClick={handleAddCategory}
            className="px-4 bg-blue-600 rounded-r text-white hover:bg-blue-700 transition-colors"
          >
            Ajouter
          </button>
        </div>

        {formData.categories.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {formData.categories.map((cat) => (
              <span
                key={cat}
                className="bg-blue-900/40 text-blue-100 px-2 py-1 rounded flex items-center"
              >
                {cat}
                <button
                  type="button"
                  onClick={() => handleRemoveCategory(cat)}
                  className="ml-2 text-xs text-blue-300 hover:text-blue-100"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className={isModal ? "flex gap-3 pt-4" : ""}>
        <button
          type="submit"
          disabled={loading || uploadingLogo}
          className={`${isModal ? 'flex-1' : 'w-full'} p-3 bg-blue-600 rounded text-white font-medium hover:bg-blue-700 transition-colors disabled:opacity-70`}
        >
          {loading || uploadingLogo ? "Chargement..." : (editingTool ? "Modifier l'outil" : (isModal ? "Ajouter l'outil" : "Ajouter à Firestore"))}
        </button>
        {isModal && (
          <button
            type="button"
            onClick={handleCancel}
            className="px-6 p-3 bg-gray-600 rounded text-white font-medium hover:bg-gray-700 transition-colors"
          >
            Annuler
          </button>
        )}
      </div>
    </form>
  );

  // Single return JSX - conditional rendering based on isModal
  return isModal ? (
    <FormContent />
  ) : (
    <div className="text-gray-100 min-h-screen flex flex-col">
      <Header
        PageName={"Add AI Tool"}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
      />
      <div className="bg-[#0a1026] flex-grow flex items-center justify-center">
        <div className="w-full max-w-xl">
          <h1 className="text-2xl font-bold mb-6">
            {editingTool ? "Modifier l'outil IA" : "Ajouter un nouvel outil IA"}
          </h1>
          <div className="bg-[#0f172a] p-6 rounded-lg shadow-lg border border-gray-800">
            <FormContent />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIToolsForm;